import { Button, Tooltip } from '@chakra-ui/react';
import { useSnapshot } from 'valtio';
import { RefreshCw } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import {
  EditorSavingStateEnum,
  dukeTTEditorState,
} from '../components/ReactFlow/dukeTTEditorState';
import { SaveStateIndicator, saveStateColour } from './SaveStateIndicator';
import { testIds } from './testIds';

const SAVE_STATE: Record<EditorSavingStateEnum, { label: string; colour: string }> = {
  [EditorSavingStateEnum.SAVED]: { label: 'Saved', colour: saveStateColour.saved },
  [EditorSavingStateEnum.SAVING]: { label: 'Saving...', colour: saveStateColour.saving },
  [EditorSavingStateEnum.UNSAVED_CHANGES]: {
    label: 'Unsaved changes',
    colour: saveStateColour.unsaved,
  },
};

/**
 * Save-state indicator shared by every editor toolbar. Reads the single source
 * of truth (`dukeTTEditorState.state`, driven by `useAutosaveFeedback`), so the
 * flow editor and the classic/unit-guide editors show the same Saving / Saved /
 * Unsaved feedback instead of each rendering their own.
 *
 * When the tab has fallen behind the server it shows a "Load latest" action in
 * place of the status: an amber button carries the stale state (colour + icon +
 * tooltip), while the label stays an action verb so it reads as "click to fix",
 * not "something is wrong". Triggering it is the caller's `onLoadLatest`, which
 * reloads (confirming first if there are unsaved changes to discard).
 */
export function EditorSaveStatus({
  isStale = false,
  entityLabel,
  onLoadLatest,
}: {
  isStale?: boolean;
  entityLabel?: string;
  onLoadLatest?: () => void;
}) {
  const { state } = useSnapshot(dukeTTEditorState);
  const status = SAVE_STATE[state];

  if (!status) {
    return null;
  }

  if (!isStale) {
    return (
      <SaveStateIndicator
        colour={status.colour}
        label={status.label}
        data-testid={testIds.saveStatus}
      />
    );
  }

  return (
    <Tooltip
      label={`A newer version of this ${entityLabel ?? 'page'} was saved elsewhere. Load it to catch up.`}
    >
      <Button
        colorScheme="orange"
        size="sm"
        leftIcon={<RefreshCw size={iconSize.sm} />}
        onClick={onLoadLatest}
        flexShrink={0}
        data-testid={testIds.loadLatestButton}
      >
        Load latest
      </Button>
    </Tooltip>
  );
}
